import { T, useGT, Var } from 'gt-react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { toast } from 'src/components/ui/toast';
import { useSendTaskBackToAgent } from 'src/api/rpc/hooks/tasks/useTaskDetail';
import { useTaskStatusLabel } from 'src/pages/authenticated/workbooks/integrations/taskPriorityLabels';
import type { SendBackAction, SendBackPlan } from './sendBackToAgentPlan';

/**
 * Confirmation for sending the task back to the agent named on its task
 * type. It lists the row changes before anything is written.
 */
export function SendTaskBackToAgentDialog({
  taskId,
  plan,
  agents,
  open,
  onOpenChange,
}: {
  taskId: string;
  plan: SendBackPlan;
  agents: Array<{ id: string; name: string }>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const gt = useGT();
  const sendBack = useSendTaskBackToAgent(taskId);

  const agentName =
    agents.find((agent) => agent.id === plan.agentId)?.name ??
    gt("the task type's agent", {
      context: "fallback when the task type's default agent name is not loaded",
    });

  async function confirm() {
    try {
      await sendBack.mutateAsync({ id: taskId });
      onOpenChange(false);
      toast.success(
        gt('Sent back to {agentName}', {
          context: 'toast after a task is sent back to its task type agent',
          agentName,
        }),
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Could not send the task back to the agent', {
              context: 'toast when sending a task back to its agent failed',
            }),
      );
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!sendBack.isPending) onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title: confirm sending a task back to the agent configured on its task type">
              Send back to <Var>{agentName}</Var>
            </T>
          </DialogTitle>
          <DialogDescription>
            <T context="dialog body: the list below is what will change when the task is sent back to its agent">
              These changes will be applied, then the agent can claim the task
              again.
            </T>
          </DialogDescription>
        </DialogHeader>
        <ul
          className="list-disc space-y-1 pl-5 text-sm text-stone-700"
          data-testid="send-back-actions"
        >
          {plan.actions.map((action) => (
            <li key={action.kind}>
              <SendBackActionLine action={action} agentName={agentName} />
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={sendBack.isPending}
            onClick={() => onOpenChange(false)}
          >
            <T context="button, close the send-task-back dialog without changing the task">
              Cancel
            </T>
          </Button>
          <Button
            type="button"
            data-testid="confirm-send-task-back"
            disabled={sendBack.isPending}
            onClick={() => {
              void confirm();
            }}
          >
            {sendBack.isPending ? (
              <T context="button label while a task is being sent back to its agent">
                Sending…
              </T>
            ) : (
              <T context="button, confirm sending the task back to its task type's default agent">
                Send Back to Agent
              </T>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SendBackActionLine({
  action,
  agentName,
}: {
  action: SendBackAction;
  agentName: string;
}) {
  const statusLabel = useTaskStatusLabel();
  switch (action.kind) {
    case 'assign':
      return (
        <T context="confirmation item: the task will be assigned to the task type's default agent">
          Assign this task to <Var>{agentName}</Var>
        </T>
      );
    case 'set_pending':
      return (
        <T context="confirmation item: the task status will change to pending">
          Change the status from <Var>{statusLabel(action.fromStatus)}</Var> to
          Pending
        </T>
      );
    case 'clear_outcome':
      return (
        <T context="confirmation item: the task's recorded outcome will be cleared when it is re-queued">
          Clear the recorded outcome
        </T>
      );
    case 'release_run':
      return (
        <T context="confirmation item: the in-progress agent run will be released so the task can be claimed again">
          Release the current run
        </T>
      );
    case 'clear_snooze':
      return (
        <T context="confirmation item: an active snooze clears because the assignee is changing">
          Clear the snooze
        </T>
      );
  }
}
